import type { RefetchQueriesInclude } from "@apollo/client";
import type {
  Document as GqlDocument,
  DocumentInput,
  DocumentType,
  FacilityDocumentInput,
  TagInput,
  TagPickerFragment,
} from "generated-graphql/graphql";
import type { Dispatch, SetStateAction } from "react";

export type ExistingDefaultDocument = {
  documentType: string;
  title: string;
  id: string;
};

export type AddEditDocumentDialogProps = {
  onSubmit?: (
    document: Pick<GqlDocument, "id" | "fileExtension" | "title" | "documentType">,
    dialogState: AddEditDocumentDialogState
  ) => void;
  dialogState: AddEditDocumentDialogState;
  setDialogState: Dispatch<SetStateAction<AddEditDocumentDialogState>>;
  documentTypeOptions?: DocumentType[];
  activityPickerOptions?: {
    id: string;
    title: string;
  }[];
  tenantId: string;
  reportId?: string;
  /** When supplied, per-tenant naming overrides can include `RY{year}` in standardized titles. */
  reportingYear?: number | null;
  state?: string | null;
  showFacilityPicker?: boolean;
  showTagPicker?: boolean;
  refetchQueries?: RefetchQueriesInclude;
  showAttestation?: boolean;
  generateTitleOnCreate?: boolean;
  isReadOnly?: boolean;
  dialogHeight?: number;
  disableDocumentTypeSelection?: boolean;
};

export enum FormState {
  UPLOAD = "Upload",
  FORM = "Form",
}

export enum EditMode {
  ADD = "Add",
  ADD_OR_PICK = "AddOrPick",
  EDIT = "Edit",
}

export type AddEditDocumentDialogState = {
  facilities?: FacilityDocumentInput[];
  documentId?: string;
  open: boolean;
  mode?: EditMode;
  step?: FormState;
  title?: string;
  documentType?: DocumentType;
  storageLink?: string;
  activityId?: string;
  fileExtension?: string | null | undefined;
  selectedFile?: File;
  documentTags?: TagPickerFragment[];
  saving: boolean;
  currentSubmissionDocument?: boolean;
  authoredAt?: string;
  description?: string | null | undefined;
  isAddingFromIssue?: boolean;
  isReadOnly?: boolean;
};

export type DocumentFormData = Omit<DocumentInput, "documentTags"> & {
  fileSize?: number;

  // gql-gen generates this attribute with type `InputMaybe<T>` which means it could be null
  // but it can only be undefined or an array of tag inputs
  documentTags?: TagInput[];
};

export const defaultAddEditDocumentDialogState: AddEditDocumentDialogState = {
  open: false,
  saving: false,
  step: FormState.UPLOAD,
};
